import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { useAuth } from '~/auth/auth-context'
import { Working } from '~/components/working'
import { useBeat } from '~/components/use-work'
import { useToast } from '~/components/toast-context'
import { TrendChart } from '~/components/trend-chart'
import { useApi } from '~/data/data-context'
import { coachedTargets, expenditureOf } from '~/data/expenditure'
import {
  DEFAULT_PROGRAM,
  isEvenWeek,
  type Goal,
  type NutritionData,
  type Program,
} from '~/data/nutrition'
import { keepGoal, reloadNutrition } from '~/data/nutrition-store'
import { todayKey, type WeighIn } from '~/data/weighins'
import type { DayValue } from '~/lib/day-value'
import { withoutDialog } from '~/lib/dialogs'
import { sayGoalReached, type Reading } from '~/lib/goal-reached'
import { formatWeight } from '~/lib/units'

/**
 * Reaching a body goal — the nutrition spec, Reaching it. The whole screen, over whatever page
 * the weigh-in was logged from, the moment the weigh-in that did it lands.
 *
 * It carries the reading that crossed and the line that reading draws over the goal, with the
 * target ruled across it so the crossing can be seen, and how far the weight came from where
 * the goal began. The ways on wait behind it: one press puts the celebration past, and the
 * choice then stands on its own, each way saying what taking it does. Nobody wants to be asked
 * what happens next in the same breath as being told they got there.
 */
export function GoalReachedScreen({
  goal,
  reading,
  scale,
  trend,
  data,
  weighIns,
  onClose,
}: {
  goal: Goal
  /** Which reading crossed: the scale weight of a weigh-in, or the trend weight. */
  reading: Reading
  /** Both series, oldest first, over every weigh-in. */
  scale: readonly DayValue[]
  trend: readonly DayValue[]
  data: NutritionData
  weighIns: readonly WeighIn[]
  onClose: () => void
}) {
  const api = useApi()
  const toast = useToast()
  const navigate = useNavigate()
  const { units, settings } = useAuth()
  const today = todayKey()
  /** The celebration first; the ways on once it has been put past. */
  const [choosing, setChoosing] = useState(false)
  const [busy, setBusy] = useState(false)
  const busyShown = useBeat(busy)

  const line = (reading === 'trend' ? trend : scale).filter(
    (point) => point.day >= goal.effectiveFrom,
  )
  const began = line[0]
  const now = line[line.length - 1]
  const came = began === undefined || now === undefined ? null : now.value - began.value

  const put = async () => {
    if (goal.targetWeightKg === null || now === undefined || busy) {
      return
    }
    setBusy(true)
    try {
      /*
       * Maintaining at the weight reached: a coached goal, from today, aiming at where the
       * body actually is rather than where the last goal was pointed. It keeps the program the
       * goal being left was set with — the diet, the floor, the protein and the spread are how
       * this person eats, and reaching a target is no reason to change any of them.
       */
      const program: Program = {
        diet: goal.diet ?? DEFAULT_PROGRAM.diet,
        calorieFloor: goal.calorieFloor ?? DEFAULT_PROGRAM.calorieFloor,
        proteinIntake: goal.proteinIntake ?? DEFAULT_PROGRAM.proteinIntake,
        distribution: goal.calorieDistribution ?? DEFAULT_PROGRAM.distribution,
      }
      const spent = expenditureOf(data, weighIns, settings, today)
      const got = coachedTargets(spent, settings, 'maintain', null, program, now.value)
      if (!('targets' in got)) {
        toast('There is not enough logged yet to work out a maintaining goal.')
        return
      }
      const days = got.targets.days.map((day) => ({ ...day }))
      keepGoal(
        await api.putGoal({
          effectiveFrom: today,
          mode: 'guided',
          aim: 'maintain',
          targetWeightKg: now.value,
          rateKgPerWeek: null,
          weeklyCheckinWeekday: goal.weeklyCheckinWeekday,
          diet: program.diet,
          calorieFloor: program.calorieFloor,
          proteinIntake: program.proteinIntake,
          calorieDistribution: [...program.distribution],
          calories: got.targets.calories,
          protein: got.targets.protein,
          carbs: got.targets.carbs,
          fat: got.targets.fat,
          expenditureAtSet: got.targets.expenditure,
          dayTargets: isEvenWeek(days) ? null : days,
        }),
      )
      reloadNutrition()
      toast('Maintaining from today.')
      onClose()
    } catch {
      toast('That did not reach us. Try again in a moment.')
    } finally {
      setBusy(false)
    }
  }

  const otherWay = goal.aim === 'lose' ? 'gain' : 'lose'
  const primary =
    'w-full rounded-full bg-accent px-5 py-3 text-sm font-medium text-accent-ink disabled:opacity-50'
  const quiet =
    'w-full rounded-full px-5 py-3 text-sm text-ink-muted transition-colors hover:bg-hover hover:text-ink disabled:opacity-50'

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Goal reached"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 overflow-y-auto bg-page px-6 py-10"
    >
      {choosing ? (
        <div className="flex w-full max-w-sm flex-col gap-5">
          <h1 className="text-center text-xl font-semibold">Where to now?</h1>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              aria-busy={busy || undefined}
              className={`relative ${primary}`}
              onClick={() => {
                void put()
              }}
            >
              <Working shown={busyShown}>
                <span className="block">Maintain here</span>
                <span className="block text-xs font-normal opacity-80">
                  A coached goal holding you at{' '}
                  {now === undefined ? 'this weight' : formatWeight(now.value, units)}, from today.
                </span>
              </Working>
            </button>
            <button
              type="button"
              disabled={busy}
              className={quiet}
              onClick={() => {
                onClose()
                void navigate({
                  to: '/you/goal',
                  search: (prev) => ({ ...withoutDialog(prev), aim: otherWay }),
                })
              }}
            >
              <span className="block">
                {otherWay === 'gain' ? 'Start gaining' : 'Start losing'}
              </span>
              <span className="block text-xs">A new goal, set from the beginning.</span>
            </button>
            <button type="button" disabled={busy} className={quiet} onClick={onClose}>
              <span className="block">Carry on as things are</span>
              <span className="block text-xs">The goal stays exactly as it is.</span>
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="text-center">
            <p className="text-sm tracking-wider text-ink-muted uppercase">
              {reading === 'trend' ? 'Your trend weight' : 'The scale'}
            </p>
            <h1 className="mt-2 text-3xl font-semibold">You got there</h1>
            <p className="mt-2 text-lg text-ink-muted">
              {goal.targetWeightKg === null
                ? 'The target is behind you.'
                : `${formatWeight(goal.targetWeightKg, units)}, reached.`}
            </p>
          </div>

          {/*
            The reading's own line over the goal, with the target ruled across it: a number
            saying it happened, and a shape showing it happening, which is the half worth
            looking at after eleven weeks of it.
          */}
          {line.length > 1 && (
            <div className="relative h-40 w-full max-w-sm">
              <TrendChart
                points={line}
                label={`${reading === 'trend' ? 'The trend weight' : 'The scale weight'} over this goal`}
                ruleAt={goal.targetWeightKg ?? undefined}
                marksLatest
                className="pointer-events-none absolute inset-0"
              />
            </div>
          )}

          {came !== null && Math.abs(came) >= 0.05 && (
            <p className="text-center text-sm text-ink-muted">
              {came < 0 ? 'Down' : 'Up'} {formatWeight(Math.abs(came), units)} since this goal
              began.
            </p>
          )}

          <button
            type="button"
            className={`${primary} max-w-sm`}
            onClick={() => {
              sayGoalReached(goal.id, reading)
              setChoosing(true)
            }}
          >
            What now?
          </button>
        </>
      )}
    </div>
  )
}
